// Reader settings — the composer's slider button, ported from iOS's ReaderSettingsSheet.
//
// Font, text size, line spacing, reading background and alignment, restyling the writing surface
// live. Display only: none of it touches the entry text, so nothing here is encrypted or synced. iOS
// keeps the same choices in UserDefaults, which is also per-device, so localStorage is the honest
// equivalent rather than a gap.
//
// The background colours are the exact hex pairs from ReaderSettings.swift. Reading in Cream on the
// phone and a different cream on the laptop would be a worse bug than not having the feature.
//
// The background is the one setting stored per entry kind rather than globally; see ReaderSettings
// below for why.

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

export type ReaderFont = 'instrument' | 'newsreader' | 'inter' | 'mono';
export type ReaderBackground = 'paper' | 'cream' | 'sage' | 'mist' | 'ink';
export type ReaderKind = 'waking' | 'dream';

/** The kind assumed by callers that have no entry open — the Settings screen, and the preview. */
const DEFAULT_KIND: ReaderKind = 'waking';

export interface ReaderSettings {
  font: ReaderFont;
  /** 13–26, matching iOS's range. */
  textSize: number;
  /** 1.0–2.4, as a multiple of the text size. */
  lineSpacing: number;
  /**
   * One background per entry kind, because the two kinds do not start from the same surface: a
   * waking entry is written on the near-white card and a dream on the dark one. A single shared
   * value meant choosing Cream for a journal entry also repainted every dream, and the default
   * each kind was designed around could never be got back for one without losing it for the other.
   *
   * Everything else in here stays global. Font and size are about the reader's eyes, not about
   * what they are reading.
   */
  backgrounds: Record<ReaderKind, ReaderBackground>;
  justified: boolean;
}

export const READER_DEFAULTS: ReaderSettings = {
  font: 'instrument',
  textSize: 17,
  lineSpacing: 1.7,
  backgrounds: { waking: 'paper', dream: 'paper' },
  justified: false,
};

const STORAGE_KEY = 'oreia.reader';

const FONTS: { value: ReaderFont; label: string; stack: string }[] = [
  { value: 'instrument', label: 'Instrument', stack: '"Instrument Serif", Georgia, serif' },
  // iOS falls back to Georgia when the Newsreader face is not bundled; the browser does the same if
  // the webfont has not arrived, so the option behaves identically in the worst case.
  { value: 'newsreader', label: 'Newsreader', stack: '"Newsreader", Georgia, serif' },
  { value: 'inter', label: 'Inter', stack: '"Inter", system-ui, sans-serif' },
  { value: 'mono', label: 'Geist Mono', stack: '"Geist Mono", ui-monospace, "SF Mono", Menlo, monospace' },
];

/** fill / ink pairs copied from ReaderBackground.colors in ReaderSettings.swift. Paper has none — it
 *  keeps the card's own surface, which differs between a waking entry and a dream. */
const BACKGROUNDS: { value: ReaderBackground; label: string; fill?: string; ink?: string }[] = [
  { value: 'paper', label: 'Paper' },
  { value: 'cream', label: 'Cream', fill: '#F6ECD7', ink: '#463C2E' },
  { value: 'sage', label: 'Sage', fill: '#E5EFE1', ink: '#35443A' },
  { value: 'mist', label: 'Mist', fill: '#E2EAF4', ink: '#334357' },
  { value: 'ink', label: 'Ink', fill: '#17161F', ink: '#ECE8DD' },
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function asBackground(value: unknown, fallback: ReaderBackground): ReaderBackground {
  return BACKGROUNDS.some((b) => b.value === value) ? (value as ReaderBackground) : fallback;
}

/**
 * Tolerant of both shapes this key has held. Before the split there was one `background` string for
 * every entry; a reader who chose Cream yesterday keeps it for both kinds rather than being reset to
 * Paper by an upgrade they did not ask for.
 */
function parseBackgrounds(o: {
  background?: unknown;
  backgrounds?: unknown;
}): Record<ReaderKind, ReaderBackground> {
  const legacy = asBackground(o.background, READER_DEFAULTS.backgrounds.waking);
  const stored = (o.backgrounds ?? {}) as Record<string, unknown>;
  return {
    waking: asBackground(stored.waking, legacy),
    dream: asBackground(stored.dream, legacy),
  };
}

function parse(raw: string | null): ReaderSettings {
  if (!raw) return READER_DEFAULTS;
  try {
    const o = JSON.parse(raw) as Partial<ReaderSettings> & { background?: unknown };
    return {
      font: FONTS.some((f) => f.value === o.font) ? (o.font as ReaderFont) : READER_DEFAULTS.font,
      textSize: clamp(Number(o.textSize) || READER_DEFAULTS.textSize, 13, 26),
      lineSpacing: clamp(Number(o.lineSpacing) || READER_DEFAULTS.lineSpacing, 1, 2.4),
      backgrounds: parseBackgrounds(o),
      justified: o.justified === true,
    };
  } catch {
    return READER_DEFAULTS;
  }
}

export function useReaderSettings(): [ReaderSettings, (patch: Partial<ReaderSettings>) => void] {
  const [settings, setSettings] = useState<ReaderSettings>(() =>
    parse(typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY)),
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const update = useCallback((patch: Partial<ReaderSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  return [settings, update];
}

/** The background chosen for one kind of entry. */
export function readerBackground(
  settings: ReaderSettings,
  kind: ReaderKind = DEFAULT_KIND,
): ReaderBackground {
  return settings.backgrounds[kind] ?? READER_DEFAULTS.backgrounds[kind];
}

/**
 * The chosen settings as CSS custom properties for the card element.
 *
 * A tinted background rebinds the ink tokens rather than colouring the text directly, so every child
 * — labels, chips, the word count — follows the reading surface instead of staying tuned for the
 * theme underneath it. The opacity steps are iOS's: 0.72 soft, 0.50 mute, 0.40 faint.
 */
export function readerVars(settings: ReaderSettings, kind: ReaderKind = DEFAULT_KIND): CSSProperties {
  const font = FONTS.find((f) => f.value === settings.font) ?? FONTS[0];
  const background = BACKGROUNDS.find((b) => b.value === readerBackground(settings, kind));

  const vars: Record<string, string> = {
    '--reader-font': font.stack,
    '--reader-size': `${settings.textSize}px`,
    '--reader-leading': String(settings.lineSpacing),
    '--reader-align': settings.justified ? 'justify' : 'left',
  };

  if (background?.fill && background.ink) {
    vars['--reader-fill'] = background.fill;
    vars['--ink'] = background.ink;
    vars['--ink-soft'] = `color-mix(in oklch, ${background.ink} 72%, transparent)`;
    vars['--ink-mute'] = `color-mix(in oklch, ${background.ink} 50%, transparent)`;
    vars['--ink-faint'] = `color-mix(in oklch, ${background.ink} 40%, transparent)`;
  }

  return vars as CSSProperties;
}

/** Whether the card should paint a flat reading tint instead of its own surface. */
export function isTinted(settings: ReaderSettings, kind: ReaderKind = DEFAULT_KIND): boolean {
  return readerBackground(settings, kind) !== 'paper';
}

// ─── The sheet ─────────────────────────────────────────────────────────

export function ReaderSettingsSheet({
  open,
  settings,
  kind,
  onChange,
  onClose,
  children,
}: {
  open: boolean;
  settings: ReaderSettings;
  /**
   * The kind of entry this sheet was opened over, which the background control belongs to.
   *
   * Left unset by the Settings screen, where no entry is open. With no kind to scope it to, a
   * background chosen there is the global choice it has always been and lands on both — better than
   * silently editing one of two values the screen gives no way to see.
   */
  kind?: ReaderKind;
  onChange: (patch: Partial<ReaderSettings>) => void;
  onClose: () => void;
  /** The entry-specific options — title, dream type, symbols, date — which iOS also puts in here. */
  children?: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Stops the composer's own Escape handler from closing the whole entry behind the sheet.
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const background = readerBackground(settings, kind);
  const chooseBackground = (value: ReaderBackground) =>
    onChange({
      backgrounds: kind
        ? { ...settings.backgrounds, [kind]: value }
        : { waking: value, dream: value },
    });

  // Portalled to <body> because this sheet now has two homes. From the composer it sat inside
  // `.ocomposer`, itself a top-level fixed layer, and everything worked. From Settings it renders
  // inside `.oscroll` — which is `position: absolute; z-index: 1` and therefore a stacking context, so
  // the sheet's z-index was resolved within that one layer and the nav pill at z-index 30 painted over
  // its lower half, including the text-alignment control.
  return createPortal(
    <>
      <div className="orset-scrim" onClick={onClose} />
      <div className="orset" role="dialog" aria-modal="true" aria-label="Reader settings">
        <div className="orset-head">
          <h2>Reader Settings</h2>
          <button className="orset-x" onClick={onClose} aria-label="Close">
            Done
          </button>
        </div>

        <div className="orset-body">
          <Section label="Preview">
            <p
              className="orset-preview"
              style={
                {
                  fontFamily: 'var(--reader-font)',
                  // The same scale the composer applies (oreia.css), so the preview is a preview
                  // rather than a smaller relative of what you are about to see.
                  fontSize: 'calc(var(--reader-size) * var(--reader-scale, 1))',
                  lineHeight: 'var(--reader-leading)',
                  textAlign: 'var(--reader-align)',
                  ...readerVars(settings, kind),
                } as CSSProperties
              }
            >
              The staircase kept rebuilding itself, and I climbed it anyway — each step arriving just
              before my foot did.
            </p>
          </Section>

          <Section label="Select font">
            <div className="odchips">
              {FONTS.map((font) => (
                <button
                  key={font.value}
                  className={`ochip${settings.font === font.value ? ' active' : ''}`}
                  style={{ fontFamily: font.stack }}
                  aria-pressed={settings.font === font.value}
                  onClick={() => onChange({ font: font.value })}
                >
                  {font.label}
                </button>
              ))}
            </div>
          </Section>

          <Section label="Typography">
            <Slider
              label="Text size"
              value={settings.textSize}
              min={13}
              max={26}
              step={1}
              display={`${settings.textSize}px`}
              onChange={(textSize) => onChange({ textSize })}
            />
            <Slider
              label="Line spacing"
              value={settings.lineSpacing}
              min={1}
              max={2.4}
              step={0.1}
              display={settings.lineSpacing.toFixed(1)}
              onChange={(lineSpacing) => onChange({ lineSpacing })}
            />
          </Section>

          {/* One control, scoped to whatever is open, rather than a control plus a switch for which
              kind it applies to — the entry on screen already says which one you mean. */}
          <Section
            label={
              kind === 'dream'
                ? 'Reading background · dreams'
                : kind === 'waking'
                  ? 'Reading background · waking'
                  : 'Reading background'
            }
          >
            <div className="orset-swatches">
              {BACKGROUNDS.map((bg) => (
                <button
                  key={bg.value}
                  className={`orset-swatch${background === bg.value ? ' on' : ''}`}
                  aria-pressed={background === bg.value}
                  onClick={() => chooseBackground(bg.value)}
                >
                  <span
                    className="orset-chip"
                    style={{
                      background:
                        bg.fill ?? 'linear-gradient(135deg, #E3D9EC, #F2E3D5)',
                    }}
                  />
                  {bg.label}
                </button>
              ))}
            </div>
          </Section>

          <Section label="Text alignment">
            <div className="odseg">
              <button
                className={`odseg-btn${settings.justified ? '' : ' on'}`}
                onClick={() => onChange({ justified: false })}
                aria-pressed={!settings.justified}
              >
                Left
              </button>
              <button
                className={`odseg-btn${settings.justified ? ' on' : ''}`}
                onClick={() => onChange({ justified: true })}
                aria-pressed={settings.justified}
              >
                Justified
              </button>
            </div>
          </Section>

          {children ? <Section label="Entry">{children}</Section> : null}
        </div>
      </div>
    </>,
    document.body,
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="orset-section">
      <span className="olabel">{label}</span>
      {children}
    </section>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (next: number) => void;
}) {
  return (
    <label className="orset-slider">
      <span className="orset-slider-head">
        <span>{label}</span>
        <span className="orset-value">{display}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
